import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ColumnType } from "antd/es/table/interface";
import { App, Typography } from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ShareAltOutlined,
  TrophyFilled,
} from "@ant-design/icons";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";

import { BaseSurvey } from "@/redux/survey/types";
import QueryTable from "@/components_v2/Tables/QueryTable";
import {
  useDeleteSurveyViewMutation,
  useLazyGetSurveyViewsWithSurveysQuery,
} from "@/redux/service";
import DropdownMenu from "@/components_v2/DropdownMenu/DropdownMenu";
import BaseButton from "@/components_v2/Button/BaseButton";
import { useSurveyViewDownload } from "@/domains/survey-views/hooks/useSurveyViewDownload";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import {
  SurveyViewConfig,
  SurveyViewPublicationDestination,
  SurveyViewWithSurvey,
} from "@/redux/survey-views/types";
import SurveyViewFilters from "@/domains/survey-views/components/SurveyViewFilters";
import { useSurveyViewPermissions } from "@/domains/survey-views/hooks/useSurveyViewPermissions";
import BaseTag from "@/components_v2/Tags/BaseTag";
import { BaseOrganizationResponse } from "@/redux/organizations/types";
import {
  SurveyViewFiltersState,
  SurveyViewTableProps,
} from "@/domains/survey-views/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

import "../styles.css";

const { Text } = Typography;

const DEFAULT_ORDERING = "-created_at";

const PUBLICATION_DESTINATION_TAGS: Record<
  SurveyViewPublicationDestination,
  { className: string; labelKey: string } | undefined
> = {
  [SurveyViewPublicationDestination.WEBSITE]: {
    className: "public",
    labelKey: "surveyViews.labels.website",
  },
  [SurveyViewPublicationDestination.IN_APP]: {
    className: "prme-community",
    labelKey: "surveyViews.labels.in_app",
  },
  [SurveyViewPublicationDestination.EXTERNAL]: {
    className: "custom",
    labelKey: "surveyViews.labels.custom",
  },
};

const PublicationDestinationTag = ({
  publicationDestination,
}: {
  publicationDestination: SurveyViewPublicationDestination;
}) => {
  const { t } = useTranslation();

  const tagConfig = PUBLICATION_DESTINATION_TAGS[publicationDestination];
  if (!tagConfig) return null;

  return (
    <BaseTag
      size="small"
      className={tagConfig.className}
      label={t(tagConfig.labelKey)}
    />
  );
};

const SurveyViewActionsCell = ({
  surveyView,
}: {
  surveyView: SurveyViewWithSurvey;
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const currentUser = useSelector(selectCurrentUser);

  const [deleteSurveyView] = useDeleteSurveyViewMutation();
  const { downloadSurveyView, isDownloading } = useSurveyViewDownload();

  const permissions = useSurveyViewPermissions(surveyView, currentUser);
  const downloading = isDownloading(surveyView.id);

  const handleDeleteSurveyView = async (surveyViewId: string) => {
    deleteSurveyView(surveyViewId)
      .unwrap()
      .then(() => {
        notification.success({
          message: t("surveyViews.labels.surveyViewDeleted"),
        });
      });
  };

  const copyToClipboard = (surveyView: SurveyViewWithSurvey) => {
    const link = `${window.location.origin}/public/reports/views/${surveyView.id}`;
    navigator.clipboard.writeText(link).then(() => {
      notification.success({ message: t("shareLinkCopied") });
    });
  };

  return (
    <DropdownMenu
      content={
        <div>
          <BaseButton
            className="dropdown-menu-button"
            isFullWidth
            variant={BUTTON_VARIANTS.LINK}
            size={BUTTON_SIZES.SMALL}
            leadingIcon={<ShareAltOutlined />}
            onClick={() => copyToClipboard(surveyView)}
          >
            {t("surveyViews.labels.copyLink")}
          </BaseButton>

          <BaseButton
            className="dropdown-menu-button"
            isFullWidth
            variant={BUTTON_VARIANTS.LINK}
            size={BUTTON_SIZES.SMALL}
            leadingIcon={
              downloading ? <BaseSpinner size="small" /> : <DownloadOutlined />
            }
            onClick={() =>
              downloadSurveyView(surveyView.id, `${surveyView.title}.pdf`)
            }
            disabled={downloading}
          >
            {downloading
              ? t("surveyViews.labels.generating")
              : t("surveyViews.labels.download")}
          </BaseButton>

          <BaseButton
            className="dropdown-menu-button"
            isFullWidth
            variant={BUTTON_VARIANTS.LINK}
            size={BUTTON_SIZES.SMALL}
            leadingIcon={<ShareAltOutlined />}
            to={"/discussions/form"}
            state={{ surveyView }}
            type={BUTTON_TYPES.LINK}
          >
            {t("surveyViews.labels.startADiscussion")}
          </BaseButton>

          {permissions.canEdit && (
            <BaseButton
              className="dropdown-menu-button"
              isFullWidth
              variant={BUTTON_VARIANTS.LINK}
              size={BUTTON_SIZES.SMALL}
              leadingIcon={<EditOutlined />}
              to={`/reports/views/${surveyView.id}/form`}
              type={BUTTON_TYPES.LINK}
            >
              {t("surveyViews.labels.edit")}
            </BaseButton>
          )}

          {permissions.canDelete && (
            <BaseButton
              className="dropdown-menu-button"
              isFullWidth
              variant={BUTTON_VARIANTS.LINK}
              size={BUTTON_SIZES.SMALL}
              leadingIcon={<DeleteOutlined />}
              onClick={() => handleDeleteSurveyView(surveyView.id)}
            >
              {t("surveyViews.labels.delete")}
            </BaseButton>
          )}
        </div>
      }
      placement="bottom"
    >
      <BaseButton variant={BUTTON_VARIANTS.LINK} size={BUTTON_SIZES.SMALL}>
        ...
      </BaseButton>
    </DropdownMenu>
  );
};

const SurveyViewsTable = ({
  columnNames,
  filterNames,
  defaultFilters = {},
  additionalColumns,
  ...props
}: SurveyViewTableProps<SurveyViewWithSurvey>) => {
  const { t } = useTranslation();

  const [filters, setFilters] =
    useState<Partial<SurveyViewFiltersState>>(defaultFilters);

  const queryFilters = useMemo(
    () => ({ ...filters, ordering: DEFAULT_ORDERING }),
    [filters],
  );

  const columns: Record<
    string,
    ColumnType<SurveyViewWithSurvey>
  > = useMemo(() => {
    const predefinedColumns: Record<
      string,
      ColumnType<SurveyViewWithSurvey>
    > = {
      title: {
        title: t("surveyViews.labels.title"),
        dataIndex: "title",
        ellipsis: true,
        width: 350,
        render: (value: string, surveyView: SurveyViewWithSurvey) => (
          <Text
            style={{ maxWidth: 350 }}
            ellipsis={{
              symbol: "...",
              tooltip: true,
            }}
          >
            <Link to={`/reports/views/${surveyView.id}`}>{value}</Link>
          </Text>
        ),
      },
      publicationDestination: {
        title: t("surveyViews.labels.publicationDestination"),
        dataIndex: ["config", "publicationDestination"],
        render: (value: SurveyViewPublicationDestination) => (
          <PublicationDestinationTag publicationDestination={value} />
        ),
      },
      organization: {
        title: t("surveyViews.labels.organization"),
        dataIndex: "organization",
        width: 200,
        render: (organization: BaseOrganizationResponse) => {
          return (
            <Text
              style={{ maxWidth: 200 }}
              ellipsis={{
                symbol: "...",
                tooltip: true,
              }}
            >
              <Link to={`/signatories/${organization.id}`}>
                {organization.name}
              </Link>
            </Text>
          );
        },
      },
      createdAt: {
        title: t("surveyViews.labels.createdAt"),
        key: "createdAt",
        dataIndex: "createdAt",
        width: 150,
        render: (createdAt: string) => {
          return DateTime.fromISO(createdAt).toFormat("dd MMMM yyyy");
        },
      },
      dataFormat: {
        title: t("surveyViews.labels.version"),
        dataIndex: "config",
        render: (config: SurveyViewConfig) =>
          t(`surveyViews.labels.${config.dataFormat.toLowerCase()}`),
      },
      survey: {
        title: t("surveyViews.labels.survey"),
        dataIndex: "survey",
        render: (value: BaseSurvey) => {
          return <Link to={`/reports/${value.id}`}>{value.title}</Link>;
        },
      },
      excellenceAward: {
        title: t("surveyViews.labels.excellenceAward"),
        key: "excellenceAward",
        dataIndex: "isExcellenceAwardWinner",
        width: 150,
        align: "center",
        render: (isWinner: boolean) =>
          isWinner ? (
            <TrophyFilled style={{ color: "#faad14", fontSize: 18 }} />
          ) : null,
      },
      actions: {
        title: t("surveyViews.labels.actions"),
        key: "actions",
        render: (value: SurveyViewWithSurvey) => (
          <SurveyViewActionsCell surveyView={value} />
        ),
      },
    };
    return { ...predefinedColumns, ...(additionalColumns || {}) };
  }, []);

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) => columns[columnKey] as ColumnType<SurveyViewWithSurvey>,
      ),
    [columns, columnNames],
  );

  return (
    <div className="survey-view-table">
      <SurveyViewFilters
        filterNames={filterNames}
        setFilters={setFilters}
        defaultFilters={defaultFilters}
      />
      <QueryTable<SurveyViewWithSurvey>
        query={useLazyGetSurveyViewsWithSurveysQuery}
        filters={queryFilters}
        columns={mappedColumns}
        {...props}
      />
    </div>
  );
};

export default SurveyViewsTable;
